"use client";

/**
 * Solid rounded-square badge with a white provenance icon.
 *
 * Extracted from the prototype `compass-intake.jsx:97` (the `ProvDot` component).
 * Replaces `.cs-pd` CSS classes with Tailwind utilities.
 */

import type { ProvenanceType } from "../lib/provenance";
import { PROV_META } from "../lib/provenance";

export interface ProvenanceDotProps {
  /** The provenance type to display. */
  type: ProvenanceType;
}

/* ---------- Tailwind class maps per provenance type ---------- */

const DOT_CLS: Record<string, string> = {
  provided: "bg-(--prov) text-white",
  estimated: "bg-(--accent-teal) text-white",
  vault: "bg-(--accent-grape) text-white",
  catalog: "bg-(--accent-grape) text-white",
  empty: "bg-(--decide) text-white",
};

/**
 * Compact provenance indicator — a solid rounded-square badge with the
 * type-specific icon in white.
 */
export function ProvenanceDot({ type }: ProvenanceDotProps) {
  // Normalize "vault" → "catalog" for the metadata lookup
  const key = type === "vault" ? "catalog" : type;
  const meta = PROV_META[key] ?? PROV_META.estimated;
  const IconComponent = meta.icon;

  return (
    <span
      className={[
        "inline-flex items-center justify-center",
        "h-[24px] w-[24px]",
        "rounded-(--radius-xs)",
        "shrink-0",
        DOT_CLS[type] ?? DOT_CLS.estimated,
      ].join(" ")}
      title={meta.label}
      role="img"
      aria-label={`Provenance: ${meta.label}`}
    >
      <IconComponent size={15} />
    </span>
  );
}
